import {startOfDay} from 'date-fns';
import {
  Actions,
  Button,
  Dialog,
  Form,
  FormField,
  FormSubmitHandler,
  openConfirmDialog,
  showNotification,
} from 'platform/components';
import {HStack, Show, Space, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNotNil} from 'ramda';

import {
  useBuyVehicleMutation,
  useCorrectVehiclePurchaseDateMutation,
  useCorrectVehicleSaleDateMutation,
  useSellVehicleMutation,
  type VehicleSalesAction,
} from '@dms/api/vehicle';
import {i18n} from '@dms/i18n';
import {
  EMPTY_PLACEHOLDER,
  handleApiError,
  usePermissions,
  useSaleVehicleActions,
} from '@dms/shared';

import {
  getApiDateString,
  localDateToUtcDate,
  Nullish,
  suffixTestId,
  TestIdProps,
  useBoolean,
} from 'shared/web';

import {getSaleDateEditMode} from '../utils/getSaleDateEditMode';

const CORRECT_SELLING_DATE_ACTION: VehicleSalesAction = 'CORRECT_SALE_DATE';
const CORRECT_BUYING_DATE_ACTION: VehicleSalesAction = 'CORRECT_PURCHASE_DATE';
const SELL_VEHICLE_ACTION: VehicleSalesAction = 'SELL_VEHICLE';
const BUY_VEHICLE_ACTION: VehicleSalesAction = 'BUY_VEHICLE';

function runDateMutation(run: () => Promise<unknown>) {
  return run()
    .then(() => {
      showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      return true;
    })
    .catch((error: Error) => {
      handleApiError(error);
      return false;
    });
}

interface VehicleSaleDatesInsightItemProps extends TestIdProps {
  vehicleId: string;
  sellingDate: Date | Nullish;
  buyingDate: Date | Nullish;
}

export function VehicleSaleDatesInsightItem(props: VehicleSaleDatesInsightItemProps) {
  const {isSaleActionEnabled} = useSaleVehicleActions(props.vehicleId);
  const formatDateTime = useDateTimeFormatter();

  const [correctSaleDate, {isLoading: isCorrectSaleDateLoading}] =
    useCorrectVehicleSaleDateMutation();
  const [correctPurchaseDate, {isLoading: isCorrectPurchaseDateLoading}] =
    useCorrectVehiclePurchaseDateMutation();
  const [sellVehicle, {isLoading: isSellVehicleLoading}] = useSellVehicleMutation();
  const [buyVehicle, {isLoading: isBuyVehicleLoading}] = useBuyVehicleMutation();

  const [canCorrectSaleDate, canCorrectPurchaseDate, canSellVehicle, canBuyVehicle] =
    usePermissions({
      permissionKeys: [
        'vehicleCorrectSaleDate',
        'vehicleCorrectPurchaseDate',
        'vehicleSell',
        'vehicleBuy',
      ],
    });

  const sellingMode = getSaleDateEditMode({
    isSaleActionEnabled,
    correctAction: CORRECT_SELLING_DATE_ACTION,
    directAction: SELL_VEHICLE_ACTION,
    canCorrect: canCorrectSaleDate,
    canDirect: canSellVehicle,
  });
  const buyingMode = getSaleDateEditMode({
    isSaleActionEnabled,
    correctAction: CORRECT_BUYING_DATE_ACTION,
    directAction: BUY_VEHICLE_ACTION,
    canCorrect: canCorrectPurchaseDate,
    canDirect: canBuyVehicle,
  });

  const handleCorrectSellingDate = (date: Date) =>
    runDateMutation(() =>
      correctSaleDate({
        vehicleId: props.vehicleId,
        correctVehicleSaleDateRequestBody: {
          saleDate: getApiDateString(localDateToUtcDate(date)),
        },
      }).unwrap()
    );

  const handleSellVehicle = (date: Date) =>
    runDateMutation(() =>
      sellVehicle({
        vehicleId: props.vehicleId,
        sellVehicleRequestBody: {
          saleDate: getApiDateString(localDateToUtcDate(date)),
        },
      }).unwrap()
    );

  const handleCorrectBuyingDate = (date: Date) =>
    runDateMutation(() =>
      correctPurchaseDate({
        vehicleId: props.vehicleId,
        correctVehiclePurchaseDateRequestBody: {
          purchaseDate: getApiDateString(localDateToUtcDate(date)),
        },
      }).unwrap()
    );

  const handleBuyVehicle = (date: Date) =>
    runDateMutation(() =>
      buyVehicle({
        vehicleId: props.vehicleId,
        buyVehicleRequestBody: {
          purchaseDate: getApiDateString(localDateToUtcDate(date)),
        },
      }).unwrap()
    );

  const handleEditSellingDate = match(sellingMode)
    .with('direct', () => handleSellVehicle)
    .with('correct', () => handleCorrectSellingDate)
    .with('none', () => undefined)
    .exhaustive();
  const handleEditBuyingDate = match(buyingMode)
    .with('direct', () => handleBuyVehicle)
    .with('correct', () => handleCorrectBuyingDate)
    .with('none', () => undefined)
    .exhaustive();

  const formattedSellingDate = props.sellingDate
    ? formatDateTime('dateShort', props.sellingDate)
    : EMPTY_PLACEHOLDER;
  const formattedBuyingDate = props.buyingDate
    ? formatDateTime('dateShort', props.buyingDate)
    : EMPTY_PLACEHOLDER;

  return (
    <VStack spacing={8}>
      <DateRow
        label={i18n.t('entity.vehicle.labels.overview.sellingDate')}
        editDialogLabel={i18n.t('entity.vehicle.labels.overview.editModal.sellingDate')}
        formattedDate={formattedSellingDate}
        date={props.sellingDate}
        onEdit={handleEditSellingDate}
        confirmationText={
          sellingMode === 'direct'
            ? i18n.t('entity.vehicle.labels.overview.editModal.irreversibleDateWarning')
            : undefined
        }
        isMutationLoading={isCorrectSaleDateLoading || isSellVehicleLoading}
        data-testid={suffixTestId('sellingDate', props)}
      />
      <DateRow
        label={i18n.t('entity.vehicle.labels.overview.buyingDate')}
        editDialogLabel={i18n.t('entity.vehicle.labels.overview.editModal.buyingDate')}
        formattedDate={formattedBuyingDate}
        date={props.buyingDate}
        onEdit={handleEditBuyingDate}
        confirmationText={
          buyingMode === 'direct'
            ? i18n.t('entity.vehicle.labels.overview.editModal.irreversibleBuyingDateWarning')
            : undefined
        }
        isMutationLoading={isCorrectPurchaseDateLoading || isBuyVehicleLoading}
        data-testid={suffixTestId('buyingDate', props)}
      />
    </VStack>
  );
}

interface DateRowProps extends TestIdProps {
  label: string;
  editDialogLabel: string;
  formattedDate: string;
  date: Date | Nullish;
  onEdit?: (date: Date) => Promise<boolean>;
  confirmationText: string | Nullish;
  isMutationLoading?: boolean;
}

function DateRow(props: DateRowProps) {
  const [isEditDialogOpen, openEditDialog, closeEditDialog] = useBoolean(false);

  const defaultDate = props.date ?? startOfDay(new Date());

  const submitDate = async (date: Date) => {
    const isSuccess = await props.onEdit?.(date);
    if (isSuccess) {
      closeEditDialog();
    }
  };

  const onSubmit: FormSubmitHandler<FormValues> = async (values) => {
    if (isNotNil(props.confirmationText)) {
      openConfirmDialog({
        text: props.confirmationText,
        onConfirm: () => submitDate(values.editDate),
        'data-testid': suffixTestId('dialog-confirmation', props),
      });
      return;
    }

    await submitDate(values.editDate);
  };

  return (
    <>
      <VStack spacing={2}>
        <Text color="secondary" size="small">
          {props.label}
        </Text>
        <HStack align="center" spacing={4}>
          <Text alternative>{props.formattedDate}</Text>
          <Show when={props.onEdit}>
            <Actions
              actions={[
                {
                  variant: 'link',
                  type: 'button',
                  leftIcon: 'image/edit',
                  title: i18n.t('general.actions.edit'),
                  onClick: openEditDialog,
                },
              ]}
              data-testid={suffixTestId('actionsWrapper', props)}
            />
          </Show>
        </HStack>
      </VStack>

      <Dialog
        isOpen={isEditDialogOpen}
        onClose={closeEditDialog}
        size="small"
        title={i18n.t('entity.vehicle.labels.overview.editModal.title')}
        data-testid={suffixTestId('dialog', props)}
      >
        <Form<FormValues>
          onSubmit={onSubmit}
          experimentalZodSchema={schema}
          defaultValues={{editDate: defaultDate}}
        >
          {(control) => (
            <>
              <FormField
                name="editDate"
                control={control}
                type="date"
                label={props.editDialogLabel}
                data-testid={suffixTestId('dialog-dateField', props)}
              />
              <Space vertical={6} />
              <HStack justify="flex-end" spacing={2}>
                <Button
                  variant="secondary"
                  onClick={closeEditDialog}
                  title={i18n.t('general.actions.discard')}
                  isDisabled={props.isMutationLoading}
                  data-testid={suffixTestId('dialog-cancel', props)}
                />
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={props.isMutationLoading}
                  title={i18n.t('general.actions.save')}
                  data-testid={suffixTestId('dialog-confirm', props)}
                />
              </HStack>
            </>
          )}
        </Form>
      </Dialog>
    </>
  );
}

const schema = z.object({
  editDate: z.date().refine((date) => date <= new Date(), {
    message: i18n.t('entity.vehicle.labels.overview.editModal.dateError'),
  }),
});

type FormValues = z.infer<typeof schema>;
